/**
 * Image processing and data preparation utilities for canvas interactions
 * Extracted from MyCanvas.js to reduce file size
 */

/**
 * Converts image element to data URL
 * @param {HTMLImageElement} image - Image element to convert
 * @returns {string|null} Data URL or null if conversion fails
 */
export const convertImageToDataUrl = (image) => {
  if (!image) return null;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width || 100;
    canvas.height = image.naturalHeight || image.height || 100;
    const ctx = canvas.getContext("2d", { alpha: true });
    ctx.drawImage(image, 0, 0);
    return canvas.toDataURL("image/png", 0.8);
  } catch (err) {
    return null;
  }
};

/**
 * Processes images for storage with proper data URL handling
 * @param {Array} images - Array of image objects
 * @returns {Promise<Array>} Promise resolving to processed images array
 */
export const processImagesForStorage = async (images) => {
  if (!images || images.length === 0) return [];

  return Promise.all(
    images.map(async (img) => {
      // CRITICAL FIX: For cropped images with baked transforms, don't convert img.image
      // because it might be loaded from the wrong URL. Instead, use the existing src/imageDataUrl
      let savedImageDataUrl;

      if (img.cropData && img.hasBakedTransforms) {
        // Image has baked transforms - use the src which should be the cropped data URL
        // If src is a data URL, use it; otherwise convert img.image
        if (img.src && img.src.startsWith("data:")) {
          savedImageDataUrl = img.src;
        } else if (img.imageDataUrl) {
          // Already have a saved imageDataUrl
          savedImageDataUrl = img.imageDataUrl;
        } else {
          // Fall back to converting img.image
          savedImageDataUrl = convertImageToDataUrl(img.image);
        }
      } else {
        // Normal image or needs conversion
        savedImageDataUrl = convertImageToDataUrl(img.image);
      }

      return {
        ...img,
        x: img.x || 0,
        y: img.y || 0,
        width: img.width || 100,
        height: img.height || 100,
        image: null, // Remove DOM references
        originalImage: null,
        compressedImage: null,
        imageDataUrl: savedImageDataUrl,
        originalImageDataUrl: convertImageToDataUrl(img.originalImage),
        // compressedImageDataUrl: convertImageToDataUrl(img.compressedImage),
      };
    })
  );
};

/**
 * Deduplicates array items by marker_id or id
 * @param {Array} items - Array to deduplicate
 * @returns {Array} Deduplicated array
 */
export const deduplicateByIdOrMarkerId = (items) => {
  if (!items || !Array.isArray(items)) return [];

  const seen = new Set();
  return items.filter((item) => {
    const key = item?.marker_id || item?.id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Prepares canvas data for storage
 * @param {Array} updatedImages - Processed images array
 * @param {Object} snapshot - Canvas snapshot
 * @param {Array} texts - Texts array
 * @param {Array} videos - Videos array
 * @param {Array} rectangles - Rectangles array
 * @param {Array} circles - Circles array
 * @param {Array} arrows - Arrows array
 * @param {Array} scribbles - Scribbles array
 * @param {Array} lines - Lines array
 * @param {Array} doubleSidedArrows - Double-sided arrows array
 * @param {number} canvasHeight - Canvas height
 * @returns {Object} Prepared canvas data object
 */
export const prepareCanvasData = (
  updatedImages,
  snapshot,
  texts,
  videos,
  rectangles,
  circles,
  arrows,
  scribbles,
  lines,
  doubleSidedArrows,
  canvasHeight
) => ({
  texts: deduplicateByIdOrMarkerId(texts),
  images: deduplicateByIdOrMarkerId(updatedImages),
  videos: deduplicateByIdOrMarkerId(videos),
  rectangles: deduplicateByIdOrMarkerId(rectangles),
  circles: deduplicateByIdOrMarkerId(circles),
  arrows: deduplicateByIdOrMarkerId(arrows),
  scribbles: deduplicateByIdOrMarkerId(scribbles),
  lines: deduplicateByIdOrMarkerId(lines),
  doubleSidedArrows: deduplicateByIdOrMarkerId(doubleSidedArrows),
  // Persist the canvas height (in viewport height percentage) for this page
  canvasHeight: parseInt(canvasHeight),
  snapshot,
});
